import React, { useState, useEffect, useRef, useMemo } from 'react'
import styled from '@emotion/styled'
import { IconButton } from '@mui/material'
import { CloseIcon } from '../../../../components/icons/CloseIcon'
import { colors } from '../../../../tokens/colors'
import { getIsLightBackground, getStringType } from '../../Misc/utils'
import { storyTypography } from '../../../../tokens/typography-storybuilder'

type Props = {
  backgroundColor?: string
  summary: { metric: string; description: string }
  hideAnimation?: boolean
  isVisible?: boolean
  handleCardClick?: () => void
  handleCardRemoval?: () => void
  index: number
}

export const SummaryCardPublic = ({
  backgroundColor,
  summary,
  isVisible,
  hideAnimation,
  handleCardClick,
  handleCardRemoval,
  index,
}: Props): JSX.Element | null => {
  const isLightBackground = useMemo(
    () => (backgroundColor ? getIsLightBackground(backgroundColor) : true),
    [backgroundColor]
  )
  const [hasStarted, setHasStarted] = useState(false)
  const [finalValue, setFinalValue] = useState<number | undefined>()
  const [currentValue, setCurrentValue] = useState<number | undefined>(0)
  const [metricType, setMetricType] = useState('')
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const intervalDurationRef = useRef<number>(20)
  const showCloseIcon = index > 0

  useEffect(() => {
    const { metric } = summary
    const returnedMetricType = getStringType(metric)
    setMetricType(returnedMetricType)
    if (metricType !== 'STRING') {
      // remove $ and % and , from the metric
      setFinalValue(parseInt(metric.replace(/[$,%]/g, ''), 10))
    }
  }, [summary, metricType])

  useEffect(() => {
    // If the animation is hidden, set the final value immediately
    if (hideAnimation || hasStarted) {
      setCurrentValue(finalValue)
      return
    }
    let newVal = 0
    // don't start the animation if the final value is 0 or if the card is not visible
    if (!finalValue || !isVisible) {
      return
    }
    setHasStarted(true)
    const startInterval = () => {
      // increase percentage by 1, increase currency by 1%
      newVal += Math.max(Math.round(finalValue * 0.01), 1)
      setCurrentValue(newVal)
      const percentage = (newVal / finalValue) * 100

      // Update interval duration
      if (percentage < 80) {
        /* empty */
      } else if (percentage < 90) {
        // Begin to slow down animation between 80% ans 90%
        intervalDurationRef.current += 8
      } else {
        // Slow down even further for the last 10%
        intervalDurationRef.current += 12 * (125 / percentage)
      }
      // Clear the interval if it has reached the limit
      if (newVal >= finalValue) {
        setCurrentValue(finalValue)
        clearInterval(intervalRef.current!)
        return
      }
      // Restart the interval if it hasn't reached the limit
      intervalRef.current = setTimeout(
        startInterval,
        intervalDurationRef.current
      )
    }

    // Start the initial interval
    intervalRef.current = setTimeout(startInterval, intervalDurationRef.current)
    // Clean up the interval on component unmount
    // eslint-disable-next-line consistent-return
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalValue, setFinalValue, isVisible, hideAnimation])

  return (
    <Summary isLightBackground={isLightBackground} onClick={handleCardClick}>
      {showCloseIcon && (
        <DeleteButton
          className='delete-button'
          onClick={handleCardRemoval}
          isLightBackground={isLightBackground}
        >
          <CloseIcon
            size='large'
            color={
              isLightBackground
                ? colors.feedback.infoDark
                : colors.feedback.infoLight
            }
          />
        </DeleteButton>
      )}
      <Metric isLightBackground={isLightBackground}>
        {metricType === 'PERCENTAGE' ? `${currentValue}%` : null}
        {currentValue && metricType === 'CURRENCY'
          ? Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
            }).format(currentValue)
          : null}
        {currentValue && metricType === 'NUMBER'
          ? Intl.NumberFormat('en-US', {
              style: 'decimal',
              minimumFractionDigits: 0,
            }).format(currentValue)
          : null}
        {metricType === 'STRING' && summary.metric}
      </Metric>
      <Description isLightBackground={isLightBackground}>
        {summary.description}
      </Description>
    </Summary>
  )
}

const Metric = styled.div<{
  isLightBackground?: boolean
}>`
  color: ${({ isLightBackground }) =>
    isLightBackground ? colors.copy.headerDark : colors.copy.headerLight};
  ${storyTypography.metric.styles}
  word-break: break-word;
`

const Description = styled.div<{
  isLightBackground?: boolean
}>`
  color: ${({ isLightBackground }) =>
    isLightBackground ? colors.copy.paragraphDark : colors.copy.paragraphLight};
  word-break: break-word;
  text-align: center;
  font-family: Source Sans Pro;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 170%; /* 34px */
`

const Summary = styled('div')<{
  isLightBackground?: boolean
}>`
  max-width: 672px;
  padding: 12px;
  border-radius: 4px;
  background: ${({ isLightBackground }) =>
    isLightBackground ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)'};
  text-align: center;
  flex: 1;
  position: relative;
  @media screen and (max-width: 1106px) {
    max-width: 100%;
  }
`

const DeleteButton = styled(IconButton)<{ isLightBackground?: boolean }>`
  background: transparent;
  position: absolute;
  border-radius: 100%;
  right: 0;
  top: 0;
  display: none;
  &:hover {
    background: ${({ isLightBackground }) =>
      isLightBackground ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)'};
    cursor: pointer;
  }
`
